import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, Building2, LifeBuoy, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import ReviewStatusBadge from "@/components/ReviewStatusBadge";

const EMPTY = { users: [], developments: [], tickets: [] };

function resultsList(r) {
  return [
    ...r.users.map((u) => ({ key: `u-${u.id}`, group: "Users", icon: User, title: u.company_name || u.name, sub: `${u.email} · ${u.role}${u.suspended ? " · suspended" : ""}`, to: u.role === "developer" ? `/admin/developer-view?id=${u.id}` : `/u/${u.id}` })),
    ...r.developments.map((d) => ({ key: `d-${d.id}`, group: "Developments", icon: Building2, title: d.name, sub: `${d.city || ""}${d.country ? `, ${d.country}` : ""}`, status: d.review_status, to: `/development/${d.id}` })),
    ...r.tickets.map((t) => ({ key: `t-${t.id}`, group: "Tickets", icon: LifeBuoy, title: t.subject, sub: `${t.user_name || t.user_email} · ${(t.status || "").replace("_", " ")}`, to: `/admin?tab=support&ticket=${t.id}` })),
  ];
}

export default function AdminSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hi, setHi] = useState(0);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(EMPTY); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q.trim())}`); setRes(data); setHi(0); }
      catch { setRes(EMPTY); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); inputRef.current?.focus(); setOpen(true); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, []);

  const items = resultsList(res);
  function go(item) { setOpen(false); setQ(""); navigate(item.to); }
  function onKeyDown(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && items[hi]) go(items[hi]);
    else if (e.key === "Escape") setOpen(false);
  }

  let lastGroup = null;
  return (
    <div className="relative w-full max-w-md" ref={ref} data-testid="admin-search">
      <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
      <input
        ref={inputRef}
        data-testid="admin-search-input"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search users, developments, tickets…"
        className="w-full pl-10 pr-14 py-2 rounded-full border border-neutral-200 bg-neutral-50 focus:bg-white focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none text-sm transition-colors"
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400 border border-neutral-200 rounded px-1.5 py-0.5 hidden sm:inline">{loading ? <Loader2 size={10} className="animate-spin" /> : "Ctrl K"}</span>
      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 mt-2 rounded-2xl border border-neutral-200 bg-white shadow-xl p-1.5 z-50 max-h-[70vh] overflow-y-auto" data-testid="admin-search-results">
          {!loading && items.length === 0 && <p className="px-3 py-6 text-center text-sm text-neutral-500" data-testid="admin-search-empty">No matches for &ldquo;{q}&rdquo;</p>}
          {items.map((it, i) => {
            const header = it.group !== lastGroup ? it.group : null;
            lastGroup = it.group;
            return (
              <React.Fragment key={it.key}>
                {header && <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.16em] text-neutral-400 font-semibold">{header}</p>}
                <button
                  type="button"
                  data-testid={`admin-search-result-${it.key}`}
                  onMouseEnter={() => setHi(i)}
                  onClick={() => go(it)}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${hi === i ? "bg-propera-light" : "hover:bg-neutral-50"}`}
                >
                  <span className="w-8 h-8 rounded-lg bg-neutral-100 text-neutral-500 flex items-center justify-center flex-shrink-0"><it.icon size={15} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-neutral-900 truncate">{it.title}</span>
                    <span className="block text-xs text-neutral-500 truncate">{it.sub}</span>
                  </span>
                  {it.status && <ReviewStatusBadge status={it.status} />}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
